/**
 * Entry point: reads the app's settings from the API, starts reporting browser errors when they
 * name where to, then renders the app. If the settings cannot be read, the page explains that
 * Morcast is unavailable instead of staying blank.
 */

import "./zod.js";
import "./styles.css";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { App } from "./App.js";
import { type AppConfig, loadAppConfig } from "./config.js";
import { startErrorReporting } from "./errors.js";

const container = document.getElementById("root");
if (!container) {
  throw new Error("index.html has no #root element");
}

let config: AppConfig | null = null;
try {
  config = await loadAppConfig();
} catch {
  config = null;
}

if (config) {
  // The app runs without error reporting if Sentry cannot be loaded.
  const options = config.errorReporting
    ? await startErrorReporting(config.errorReporting).catch(() => ({}))
    : {};
  createRoot(container, options).render(
    <StrictMode>
      <App config={config} />
    </StrictMode>,
  );
} else {
  createRoot(container).render(
    <main className="mx-auto max-w-xl px-6 py-24 text-center text-zinc-900 dark:text-zinc-100">
      <h1 className="text-2xl font-semibold">Morcast is unavailable right now</h1>
      <p className="mt-3 text-zinc-600 dark:text-zinc-400">Please reload the page in a moment.</p>
    </main>,
  );
}
